/**
 * scheduler.ts — cooperative main-thread slicing (the loading round).
 *
 * The city build, the garage's model staging and the race warm-up are all
 * "many medium steps" jobs. Run back-to-back they were one multi-second task
 * and every tap during it sat in the input queue until the whole job was
 * done — the "my click didn't register" report. `runSteps` runs the same
 * steps in the same order but yields to the event loop whenever a slice has
 * used its time budget, so input events, rAF and layout interleave.
 *
 * `yieldToMain` prefers the Scheduling API (`scheduler.yield`, Chromium —
 * continuation runs at high priority, ahead of other timers) and falls back
 * to a MessageChannel hop (Safari/WebKit: a true macrotask without the ≥4 ms
 * clamped setTimeout nesting delay). Both let pointer events dispatch.
 */

interface SchedulerLike {
  yield?: () => Promise<void>;
}

const channel = typeof MessageChannel !== 'undefined' ? new MessageChannel() : null;
const hopQueue: (() => void)[] = [];
if (channel) {
  channel.port1.onmessage = () => {
    const fn = hopQueue.shift();
    fn?.();
  };
}

/** Resolve on a fresh macrotask — after pending input events have dispatched. */
export function yieldToMain(): Promise<void> {
  const sched = (globalThis as { scheduler?: SchedulerLike }).scheduler;
  if (sched?.yield) return sched.yield();
  if (channel) {
    return new Promise((resolve) => {
      hopQueue.push(resolve);
      channel.port2.postMessage(0);
    });
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** A clean requestAnimationFrame beat — or a 120 ms timer, whichever comes
 *  first (a hidden tab freezes rAF; a loading pipeline waiting on one would
 *  otherwise stall until the tab is fronted again). Headless tests hop. */
export function nextFrame(): Promise<void> {
  if (typeof requestAnimationFrame === 'function') {
    return new Promise((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        resolve();
      };
      requestAnimationFrame(finish);
      setTimeout(finish, 120);
    });
  }
  return yieldToMain();
}

export interface RunStepsOptions {
  /** Max main-thread time per slice before yielding (ms). */
  budgetMs?: number;
  /** Called after each step with (done, total). */
  onStep?: (done: number, total: number) => void;
  /** Return true to abandon the run (e.g. the job was superseded). */
  cancelled?: () => boolean;
  /** Awaited before EVERY step — the idle gate for background jobs (a step
   *  never starts while the player is mid-tap / a screen is animating in). */
  beforeStep?: () => Promise<void>;
}

/**
 * Run `steps` in order, yielding between steps once a slice has used
 * `budgetMs`. Resolves `true` when every step ran, `false` when cancelled.
 * A step that throws aborts the run (the error propagates to the caller).
 */
export async function runSteps(
  steps: (() => void)[],
  opts: RunStepsOptions = {},
): Promise<boolean> {
  const budget = opts.budgetMs ?? 12;
  const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  let sliceStart = now();
  for (let i = 0; i < steps.length; i++) {
    if (opts.cancelled?.()) return false;
    if (opts.beforeStep) {
      await opts.beforeStep();
      if (opts.cancelled?.()) return false;
      sliceStart = now();
    }
    steps[i]();
    opts.onStep?.(i + 1, steps.length);
    if (now() - sliceStart >= budget && i < steps.length - 1) {
      await yieldToMain();
      sliceStart = now();
    }
  }
  return !opts.cancelled?.();
}
